import React, { useEffect, useState } from 'react';
import type { ClassNameProp, Scale } from '../common-types';
import { Maskable } from './LoadMask';
import { defaultIndividualProviderAvatarPath, isEmptyString } from '../../util';
import { NameCircle } from './NameCircle';
import { showError } from '../../lib';
import type { ValidIconName } from './Icon';
import { Icon } from './Icon';

/**
 * Properties for a {@link PersonImage} component.
 *
 */
export interface PersonImageProps extends ClassNameProp {
  /**
   * image to display.
   */
  image?: string;
  /**
   * Method for loading an image asynchronously.
   */
  loadImage?: () => Promise<string | null | undefined>;
  /**
   * Determines how the component will behave if no image for displaying is present.
   * - 'nameCircle' {@link NameCircle} component with {@link firstName} and {@link lastName}
   * - 'defaultAvatar' img tag with the default avatar image
   * - 'IconAvatars' any valid icon name that is added in this type
   */
  emptyState: 'nameCircle' | 'defaultAvatar' | IconAvatars;
  /**
   * Person firstName for empty 'nameCircle' state.
   */
  firstName?: string;
  /**
   * Person lastName for empty 'nameCircle' state.
   */
  lastName?: string;
  scale?: Scale;
  /**
   * Alt text for the image element. If not provided, a default value is used.
   */
  alt?: string;
}

/* All allowed icons that can be used, can be extended by any ValidIconName if needed */
type IconAvatars = 'user-3' | 'user-group';

/**
 * Displays a person's image in a nice dynamic way and handles showing an 'empty' state
 * when no image is present.
 *
 * @param properties a {@link PersonImageProps} object
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
// overload to make sure firstName and lastName is required in 'nameCircle' empty mode
export function PersonImage(
  properties: PersonImageProps & {
    emptyState: 'nameCircle';
    firstName: string;
    lastName: string;
  }
): React.JSX.Element;

// overload to make sure firstName and lastName is required when the emptyState is dynamic
export function PersonImage(
  properties: PersonImageProps & {
    emptyState: 'nameCircle' | 'defaultAvatar' | IconAvatars;
    firstName: string;
    lastName: string;
  }
): React.JSX.Element;

export function PersonImage(
  properties: PersonImageProps & {
    emptyState: 'defaultAvatar' | IconAvatars;
    firstName?: string;
    lastName?: string;
  }
): React.JSX.Element;

// implementation
export function PersonImage({
  image = '',
  loadImage,
  emptyState = 'nameCircle',
  firstName,
  lastName,
  className = '',
  scale = 'medium',
  alt,
  ...props
}: Readonly<PersonImageProps>): React.JSX.Element {
  // internal state for using `loadImage`
  const [loadedImage, setLoadedImage] = useState('');
  const [loadingError, setLoadingError] = useState(false);
  const [loading, setLoading] = useState(false);

  // load person's profile picture
  useEffect(() => {
    if (loadImage) {
      setLoading(true);
      loadImage()
        .then(value => {
          if (value) {
            setLoadedImage(value);
          }
        })
        .catch(showError)
        .finally(() => setLoading(false));
    }
  }, [loadImage]);

  const shownImage = loadedImage || image;
  const clazz = 'person-image-image';
  const imgAlt = alt ?? 'person-image.png';

  const getImageFallback = () => {
    switch (emptyState) {
      case 'nameCircle':
        return (
          <NameCircle
            className={clazz}
            firstName={firstName}
            lastName={lastName}
          />
        );
      case 'defaultAvatar':
        return (
          <img
            src={defaultIndividualProviderAvatarPath}
            alt={imgAlt}
            aria-hidden
            className={clazz}
          />
        );
      default:
        return (
          <div className="icon-avatar">
            <Icon aria-label="" iconName={emptyState as ValidIconName} />
          </div>
        );
    }
  };

  return (
    <Maskable
      {...props}
      masked={loading}
      className={`ehs-person-image ${className} image-size-${scale}`.trim()}
    >
      {isEmptyString(shownImage) || loadingError ? getImageFallback() : null}
      {!isEmptyString(shownImage) ? (
        <img
          src={
            shownImage?.startsWith('http://') ||
            shownImage?.startsWith('https://')
              ? shownImage
              : `data:image/png;base64,${shownImage}`
          }
          alt={imgAlt}
          aria-hidden
          className={clazz}
          style={{ display: loadingError ? 'none' : 'block' }}
          onLoad={e => {
            const img = e.target as HTMLImageElement;
            setLoadingError(img.naturalWidth <= 1 || img.naturalHeight <= 1);
          }}
          onError={() => setLoadingError(true)}
        />
      ) : null}
    </Maskable>
  );
}
